import { debounce } from "lodash";
import React, {
  CSSProperties,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type AutoFitTextProps = {
  tag?: string | React.ElementType;
  children: ReactNode;
  minFontSize?: number;
  maxFontSize?: number;
  maxHeight?: number | string;
  maxWidth?: number | string;
  className?: string;
  style?: CSSProperties;
};

export default React.memo(function AutoFitText({
  tag,
  children,
  minFontSize: _minFontSize,
  maxFontSize: _maxFontSize,
  maxHeight,
  maxWidth,
  className,
  style,
}: AutoFitTextProps) {
  const Component = tag ?? "div";
  const minFontSize = _minFontSize ?? 12;
  const maxFontSize = _maxFontSize ?? 32;
  const ref = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);
  const [scrollable, setScrollable] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const fitText = () => {
      console.log("recalculating font size for fittext")
      element.style.fontSize = `${maxFontSize}px`;

      let size = maxFontSize;

      while (
        element.scrollHeight > element.clientHeight &&
        size > minFontSize
      ) {
        size -= 1;
        element.style.fontSize = `${size}px`;
      }

      setFontSize(size);
      setScrollable(element.scrollHeight > element.clientHeight);
    };

    // run once with the current content
    fitText();

    // add a mutation observer for future changes
    const observer = new MutationObserver(fitText);
    observer.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // also respond to window resize events
    // TODO: Only recalculate if the bounding box of the div changed
    let { width: lastWidth, height: lastHeight } = element.getBoundingClientRect();
    const handleResize = debounce(() => {
      const { width, height } = element.getBoundingClientRect();
      if (width === lastWidth && height === lastHeight) {
        return;
      }

      console.log("window size changed, recalculate fittext")
      lastWidth = width;
      lastHeight = height;
      fitText();
    }, 500);
    window.addEventListener('resize', handleResize);

    // disconnect on unmount
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [minFontSize, maxFontSize, maxHeight, maxWidth]);

  return (
    <Component
      ref={ref}
      className={className}
      style={{
        ...style,
        maxHeight,
        maxWidth,
        fontSize,
        overflowY: scrollable ? "auto" : "hidden",
      }}
    >
      {children}
    </Component>
  );
})